import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import classNames from "classnames";
import {
    hasPaginationControls,
    Pagination,
} from "@design-system/Pagination/Pagination";
import { useDataGridContext } from "../DataGridContext";
import type { GridController } from "../focus/gridController";
import type { DataGridInstance } from "../types";
import styles from "./toolbar.module.scss";

interface DataGridPagerProps {
    className?: string;
}

interface PagerControlsProps<TData extends RowData> extends DataGridPagerProps {
    instance: DataGridInstance<TData>;
    controller: GridController;
}

const PagerControls = <TData extends RowData>({
    instance,
    controller,
    className,
}: Readonly<PagerControlsProps<TData>>) => {
    const { table } = instance;
    const { pageIndex } = useSelector(table.atoms.pagination);
    const pageCount = table.getPageCount();
    const lastPage = Math.max(pageCount, 1);
    if (!hasPaginationControls({ pageCount, currentPage: pageIndex + 1 })) {
        return null;
    }
    return (
        <Pagination
            size="md"
            className={classNames(styles.pager, className)}
            pageCount={pageCount}
            currentPage={pageIndex + 1}
            shouldScrollWindow={false}
            onPageChange={(page) => {
                table.setPageIndex(Math.min(page, lastPage) - 1);
                controller.viewport?.scrollTo({ top: 0 });
            }}
        />
    );
};

/**
 * The design-system `Pagination` (previous and next buttons, a page input,
 * and the page count) for client and server pagination; hidden with one page
 * unless the page is past the last one, so the user can get back. Server
 * tables refetch from `pagination` state changes.
 */
export const DataGridPager = (props: Readonly<DataGridPagerProps>) => {
    const { visit, controller } = useDataGridContext();
    return visit((instance) => (
        <PagerControls {...props} instance={instance} controller={controller} />
    ));
};
